import { useAppDispatch } from 'app/hooks';
import { AxiosError } from 'axios';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { MenuBookListItem } from 'features/MenuBook/models/menuBook';
import { EatingWayDataSource, Restaurant, StoreSettingDetailGetAllRequestPayload } from 'features/Store/models';
import { TimeFrame } from 'features/Store/models/timeframe';
import storeSettingDetailService from 'features/Store/storeSettingDetailService';
import { storeSettingDetailActions } from 'features/Store/storeSettingDetailSlice';
import timeFrameService from 'features/Store/timeFrameService';
import masterDataService from 'features/common/masterDataService';
import { DisplayTimingMap } from 'features/common/models';
import moment from 'moment';
import { FormEvent, forwardRef, useEffect, useRef, useState } from 'react';
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { DATE_FORMAT_YYYY_MM_DD, displayError, handleServiceError } from 'utils';

const CustomInputDatePicker = forwardRef((props: any, ref: any) => {
  // You can style this further
  return (
    <>
      <div style={{ position: 'relative' }}>
        <FaCalendarAlt style={{ position: 'absolute', top: '8px', left: '10px' }} onClick={props.onClick} />
        <input
          className="form-control text-center"
          onClick={props.onClick}
          style={{ paddingLeft: '32px' }}
          value={props.value}
          onChange={props.onChange}
          type="text"
        />
      </div>
    </>
  );
});

const SEARCH_SHOW_ASSIGNED_KEY_DEFAULT = '1';

export interface StoreSettingDetailFilterProps {
  restaurant: Restaurant;
  onSubmit?: (payload: StoreSettingDetailGetAllRequestPayload) => void;
}

interface FormSearchValueProps {
  timeFrameId: string;
  eatingWayId: string;
  assignedStatus: '1' | '2';
  assignedTimeFrameId: string;
  menuBookId: string;
  courseId: string;
  dataDate: Date;
}

function StoreSettingDetailFilter({ restaurant, onSubmit }: StoreSettingDetailFilterProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { showGlobalModal } = useGlobalModal();
  // const history = useHistory();

  const [isLoadingFilter, setIsLoadingFilter] = useState(true);
  // const [restaurant, setRestaurant] = useState<Restaurant>();
  const [timeFrameSource, setTimeFrameSource] = useState<TimeFrame[]>();
  const [eatingWaySource, setEatingWaySource] = useState<EatingWayDataSource[]>();
  const [assignedTimeFrameSource, setAssignedTimeFrameSource] = useState<TimeFrame[]>();
  const [menuBookMap, setMenuBookMap] = useState<{ [key: string]: MenuBookListItem }>();
  const [courseMap, setCourseMap] = useState<{ [key: string]: string }>();
  const [errorTimeFrameText, setErrorTimeFrameText] = useState<string>();
  const [errorEatingWayText, setErrorEatingWayText] = useState<string>();
  const [errorAssignedTimeFrameText, setErrorAssignedTimeFrameText] = useState<string>();
  const [errorMenuBookText, setErrorMenuBookText] = useState<string>();

  // ref
  const formSearchRef = useRef<HTMLFormElement>(null);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    timeFrameId: '',
    eatingWayId: '',
    assignedStatus: SEARCH_SHOW_ASSIGNED_KEY_DEFAULT,
    // showFlg: '1',
    assignedTimeFrameId: '',
    menuBookId: '',
    courseId: '',
    dataDate: new Date(),
  });
  const eatingWayRef = useRef<HTMLSelectElement>(null);
  const assignedTimeFrameRef = useRef<HTMLSelectElement>(null);
  const menuBookRef = useRef<HTMLSelectElement>(null);
  const courseRef = useRef<HTMLSelectElement>(null);

  const SHOW_ASSIGNED_SOURCE: { [key: string]: string } = {
    // '0': t('StoreSettingDetailPage.assignment_status_show_all'),
    '1': t('StoreSettingDetailPage.assignment_status_assigned'),
    '2': t('StoreSettingDetailPage.assignment_status_unassigned'),
  };

  function loadInitData() {
    (async () => {
      try {
        const timeFrameResponse = await timeFrameService.getAll({ sale_type_id: restaurant.sale_type_id || undefined });
        setTimeFrameSource(timeFrameResponse.data);

        // load display_timing
        const response = await masterDataService.getDisplayTiming();
        const displayTimingMap = response.data.display_timing.reduce((displayTimingMap: DisplayTimingMap, timing) => {
          displayTimingMap[timing.id] = timing.name;
          return displayTimingMap;
        }, {});
        dispatch(storeSettingDetailActions.setDisplayTimingSource(displayTimingMap));

        // load eating_way follow time_frame
        if (timeFrameResponse.data[0].id) {
          await loadEatingWayDataSource(restaurant, timeFrameResponse.data[0].id);
        }

        setIsLoadingFilter(false);
      } catch (e) {
        // console.log(e);
      }
    })();
  }

  useEffect(() => {
    loadInitData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoadingFilter) {
      const payload = composeFilterPayload();
      if (payload) {
        const isValidFilterData = checkValidFilterRequestData(payload, false);
        if (isValidFilterData) {
          onSubmit?.(payload);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingFilter]);

  function composeFilterPayload(): StoreSettingDetailGetAllRequestPayload | undefined {
    if (formSearchRef.current) {
      const timeFrameId = Number(formSearchRef.current['time_frame'].value.trim()) || 0;
      const eatingWayId = Number(formSearchRef.current['eating_way'].value.trim()) || 0;
      const assignedStatus = Number(formSearchRef.current['assigned_status'].value.trim());
      const assignedTimeFrameId = Number(formSearchRef.current['assigned_time_frame'].value.trim()) || 0;
      const menuBookId = Number(formSearchRef.current['menu_book'].value.trim()) || 0;
      const courseId = Number(formSearchRef.current['course'].value.trim()) || 0;

      // compose request payload
      const payload: StoreSettingDetailGetAllRequestPayload = {
        restaurant_id: Number(restaurantId) || 0,
        eating_way_id: Number(eatingWayId),
        time_frame_id: timeFrameId,
        assigned_status: assignedStatus,
        data_date: formSearchValue.dataDate,
      };
      if (assignedStatus === 1) {
        payload.assigned_time_frame_id = assignedTimeFrameId;
        payload.menu_book_id = menuBookId;
        payload.course_id = courseId === 0 ? undefined : courseId;
      }

      return payload;
    }

    return undefined;
  }

  function checkValidFilterRequestData(
    payload: StoreSettingDetailGetAllRequestPayload,
    isShowFilterDataError: boolean = false
  ) {
    // validate filter data (required)
    let isValidFilterData = true;
    if (payload.time_frame_id === 0) {
      isShowFilterDataError && setErrorTimeFrameText(t('StoreSettingDetailPage.warn_filter_time_frame_required'));
      isValidFilterData = false;
    } else {
      setErrorTimeFrameText(undefined);
    }

    if (payload.eating_way_id === 0) {
      isShowFilterDataError && setErrorEatingWayText(t('StoreSettingDetailPage.warn_filter_eating_way_required'));
      isValidFilterData = false;
    } else {
      setErrorEatingWayText(undefined);
    }

    if (payload.assigned_time_frame_id === 0) {
      isShowFilterDataError &&
        setErrorAssignedTimeFrameText(t('StoreSettingDetailPage.warn_filter_assigned_time_frame_required'));
      isValidFilterData = false;
    } else {
      setErrorAssignedTimeFrameText(undefined);
    }

    if (payload.menu_book_id === 0) {
      isShowFilterDataError && setErrorMenuBookText(t('StoreSettingDetailPage.warn_filter_menu_book_required'));
      isValidFilterData = false;
    } else {
      setErrorMenuBookText(undefined);
    }

    return isValidFilterData;
  }

  function saveFormSearchValues() {
    if (formSearchRef.current) {
      setFormSearchValue({
        ...formSearchValue,
        timeFrameId: formSearchRef.current['time_frame'].value.trim(),
        eatingWayId: formSearchRef.current['eating_way'].value.trim(),
        assignedStatus: formSearchRef.current['assigned_status'].value.trim(),
        assignedTimeFrameId: formSearchRef.current['assigned_time_frame'].value.trim(),
        menuBookId: formSearchRef.current['menu_book'].value.trim(),
        courseId: formSearchRef.current['course'].value.trim(),
      });
    }
  }

  function handleSearchTimeFrameChanged() {
    resetErrorFilterRequestData();
    if (formSearchRef.current && restaurant) {
      const timeFrameId = Number(formSearchRef.current['time_frame'].value);
      loadEatingWayDataSource(restaurant, timeFrameId);
    }
  }

  // Filter Eating Way Events
  async function loadEatingWayDataSource(restaurantData: Restaurant, timeFrameId: number) {
    try {
      const response = await storeSettingDetailService.getEatingWay(timeFrameId);
      setEatingWaySource(response.data);

      //load assigned timeframe
      if (response.data.length > 0 && restaurantData.sale_type_id) {
        await loadAssignedTimeFrameDataSource(restaurantData.sale_type_id, response.data[0].id);
      } else {
        setAssignedTimeFrameSource([]);
        setMenuBookMap({});
        setCourseMap({});
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  useEffect(() => {
    if (eatingWayRef.current) {
      if (eatingWaySource && eatingWaySource.length > 0) {
        const firstKey = eatingWaySource[0].id;
        eatingWayRef.current.value = firstKey.toString();
      } else {
        eatingWayRef.current.value = '0';
      }
    }
  }, [eatingWaySource]);

  function handleSearchEatingWayChanged() {
    resetErrorFilterRequestData();
    if (formSearchRef.current && restaurant?.sale_type_id) {
      const eatingWayid = Number(formSearchRef.current['eating_way'].value);
      loadAssignedTimeFrameDataSource(restaurant.sale_type_id, eatingWayid);
    }
  }

  // Filter Assigned Time Frame Events
  async function loadAssignedTimeFrameDataSource(saleTypeId: number, eatingWayId: number) {
    try {
      const response = await timeFrameService.getAll({ sale_type_id: saleTypeId, eating_way_id: eatingWayId });
      setAssignedTimeFrameSource(response.data);

      //load search menubook data
      if (response.data.length > 0) {
        await loadMenuBookCourseDataSource(response.data[0].id, eatingWayId);
      } else {
        setMenuBookMap({});
        setCourseMap({});
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  useEffect(() => {
    if (assignedTimeFrameRef.current) {
      if (assignedTimeFrameSource && assignedTimeFrameSource.length > 0) {
        const firstKey = assignedTimeFrameSource[0].id;
        assignedTimeFrameRef.current.value = firstKey.toString();
      } else {
        assignedTimeFrameRef.current.value = '0';
      }
    }
  }, [assignedTimeFrameSource]);

  function handleSearchAssignedTimeFrameChanged() {
    resetErrorFilterRequestData();
    if (formSearchRef.current) {
      const assignedTimeFrameId = Number(formSearchRef.current['assigned_time_frame'].value);
      const eatingWayid = Number(formSearchRef.current['eating_way'].value);
      loadMenuBookCourseDataSource(assignedTimeFrameId, eatingWayid);
    }
  }

  // Filter MenuBook Events
  async function loadMenuBookCourseDataSource(assignedTimeFrameId: number, eatingWayId: number) {
    try {
      const response = await storeSettingDetailService.getMenuBookByEatingWaySetting(assignedTimeFrameId, eatingWayId);
      const menuBookMap = response.data.reduce((menuBookMap: { [key: string]: MenuBookListItem }, menuBook) => {
        menuBookMap[menuBook.id] = menuBook;
        return menuBookMap;
      }, {});
      setMenuBookMap(menuBookMap);

      //load course
      if (response.data.length > 0) {
        loadCourseDataSource(menuBookMap, response.data[0].id);
      } else {
        setCourseMap({});
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  useEffect(() => {
    if (menuBookRef.current) {
      if (menuBookMap && Object.keys(menuBookMap).length > 0) {
        const firstKey = Object.keys(menuBookMap)[0];
        menuBookRef.current.value = firstKey;
      } else {
        menuBookRef.current.value = '0';
      }
    }
  }, [menuBookMap]);

  function handleSearchMenuBookChanged() {
    resetErrorFilterRequestData();
    if (formSearchRef.current && menuBookMap) {
      const menuBookId = Number(formSearchRef.current['menu_book'].value);
      loadCourseDataSource(menuBookMap, menuBookId);
    }
  }

  // Filter Course Events
  function handleSearchCourseChanged() {
    resetErrorFilterRequestData();
  }

  function loadCourseDataSource(menuBookMap: { [key: string]: MenuBookListItem }, menuBookId: number) {
    if (menuBookMap?.[menuBookId]) {
      const courses = menuBookMap[menuBookId].courses;
      const courseMap = courses.reduce((courseMap: { [key: string]: string }, course) => {
        courseMap[course.id] = course.name;
        return courseMap;
      }, {});
      setCourseMap(courseMap);
    } else {
      setCourseMap({});
    }
  }

  useEffect(() => {
    if (courseRef.current) {
      courseRef.current.value = '0';
    }
  }, [courseMap]);

  function resetErrorFilterRequestData() {
    setErrorTimeFrameText(undefined);
    setErrorEatingWayText(undefined);
    setErrorAssignedTimeFrameText(undefined);
    setErrorMenuBookText(undefined);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const payload = composeFilterPayload();
    if (payload) {
      const isValidFilterData = checkValidFilterRequestData(payload, true);
      if (isValidFilterData) {
        // dispatch(storeSettingDetailActions.fetchData(payload));
        onSubmit?.(payload);
      }
    }
  }

  return (
    <form ref={formSearchRef} className="mb-3" onSubmit={handleSubmit}>
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{t('StoreSettingDetailPage.filter_condition')}</Accordion.Header>
          <Accordion.Body onExited={saveFormSearchValues}>
            <Row>
              <Col sm={3}>
                <Row>
                  <Col>
                    <div>
                      <Form.Label className="col-form-label">{t('StoreSettingDetailPage.time_frame')}：</Form.Label>
                    </div>
                    <div>
                      <Form.Select className="text-center" name="time_frame" onChange={handleSearchTimeFrameChanged}>
                        {timeFrameSource &&
                          timeFrameSource.map(function (timeFrame) {
                            return (
                              <option key={timeFrame.id} value={timeFrame.id}>
                                {timeFrame.name}
                              </option>
                            );
                          })}
                      </Form.Select>
                      <div className="text-danger mt-1">{errorTimeFrameText}</div>
                    </div>
                  </Col>
                  <Col>
                    <div>
                      <Form.Label className="col-form-label">{t('StoreSettingDetailPage.eating_way')}：</Form.Label>
                    </div>
                    <div>
                      <Form.Select
                        ref={eatingWayRef}
                        className="text-center"
                        name="eating_way"
                        onChange={handleSearchEatingWayChanged}
                      >
                        {(eatingWaySource === undefined || eatingWaySource.length === 0) && (
                          <option value="0">{t('StoreSettingDetailPage.no_option')}</option>
                        )}
                        {eatingWaySource &&
                          eatingWaySource.map(function (eatingWay) {
                            return (
                              <option key={eatingWay.id} value={eatingWay.id}>
                                {eatingWay.name}
                              </option>
                            );
                          })}
                      </Form.Select>
                      <div className="text-danger mt-1">{errorEatingWayText}</div>
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col sm={5}>
                <fieldset className="border rounded-3 px-3">
                  <legend className="float-none small w-auto px-3">
                    {t('StoreSettingDetailPage.eating_way_setting')}
                  </legend>
                  <Row className="my-0">
                    <Col></Col>
                  </Row>
                  <div className="mb-3">
                    <div>
                      <Form.Check
                        key={`menu-assigned-status-1`}
                        className="col-form-label1 form-label1 py-1"
                        type="radio"
                        name="assigned_status"
                        id={`menu-assigned-status-1`}
                        value={'1'}
                        defaultChecked={'1' === SEARCH_SHOW_ASSIGNED_KEY_DEFAULT}
                        label={SHOW_ASSIGNED_SOURCE['1']}
                      />
                    </div>
                    <div className="ps-4">
                      <Row>
                        <Col>
                          <div>
                            <Form.Label className="col-form-label">
                              {t('StoreSettingDetailPage.assigned_time_frame')}：
                            </Form.Label>
                          </div>
                          <div>
                            <Form.Select
                              ref={assignedTimeFrameRef}
                              className="text-center"
                              name="assigned_time_frame"
                              onClick={handleSearchAssignedTimeFrameChanged}
                            >
                              {(assignedTimeFrameSource === undefined || assignedTimeFrameSource.length === 0) && (
                                <option value="0">{t('StoreSettingDetailPage.no_option')}</option>
                              )}
                              {assignedTimeFrameSource &&
                                assignedTimeFrameSource.map(function (assignedTimeFrame) {
                                  return (
                                    <option key={assignedTimeFrame.id} value={assignedTimeFrame.id}>
                                      {assignedTimeFrame.name}
                                    </option>
                                  );
                                })}
                            </Form.Select>
                            <div className="text-danger mt-1">{errorAssignedTimeFrameText}</div>
                          </div>
                        </Col>
                        <Col>
                          <div>
                            <Form.Label className="col-form-label">
                              {t('StoreSettingDetailPage.menu_book')}：
                            </Form.Label>
                          </div>
                          <div>
                            <Form.Select
                              ref={menuBookRef}
                              className="text-center"
                              name="menu_book"
                              onChange={handleSearchMenuBookChanged}
                            >
                              {(menuBookMap === undefined || Object.keys(menuBookMap).length === 0) && (
                                <option value="0">{t('StoreSettingDetailPage.no_option')}</option>
                              )}
                              {menuBookMap &&
                                Object.keys(menuBookMap).map(function (key) {
                                  return (
                                    <option key={key} value={key}>
                                      {menuBookMap[key].name}
                                    </option>
                                  );
                                })}
                            </Form.Select>
                            <div className="text-danger mt-1">{errorMenuBookText}</div>
                          </div>
                        </Col>
                        <Col>
                          <div>
                            <Form.Label className="col-form-label">{t('StoreSettingDetailPage.course')}：</Form.Label>
                          </div>
                          <div>
                            <Form.Select
                              ref={courseRef}
                              className="text-center"
                              name="course"
                              onChange={handleSearchCourseChanged}
                            >
                              <option value="0">{t('StoreSettingDetailPage.no_option')}</option>
                              {courseMap &&
                                Object.keys(courseMap).map(function (key) {
                                  return (
                                    <option key={key} value={key}>
                                      {courseMap[key]}
                                    </option>
                                  );
                                })}
                            </Form.Select>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div>
                      <Form.Check
                        key={`menu-assigned-status-2`}
                        className="col-form-label1 form-label1 py-2"
                        type="radio"
                        name="assigned_status"
                        id={`menu-assigned-status-2`}
                        value={'2'}
                        label={SHOW_ASSIGNED_SOURCE['2']}
                      />
                    </div>
                  </div>
                </fieldset>
              </Col>

              <Col sm={3}>
                <fieldset className="border rounded-3 px-3">
                  <legend className="float-none small w-auto px-3">{t('StoreSettingDetailPage.menu')}</legend>
                  <Row>
                    <Col>
                      <fieldset className="border rounded-3 px-3 mb-3">
                        <legend className="float-none small w-auto px-3">
                          {t('StoreSettingDetailPage.related_pos')}
                        </legend>

                        <Row>
                          <Col sm={4} className="text-end">
                            <Form.Label htmlFor="txt-menu-book-name" className="col-form-label">
                              {t('StoreSettingDetailPage.deadline')}：
                            </Form.Label>
                          </Col>
                          <Col sm={8} className="px-0">
                            <Form.Label className="col-form-label">
                              {t('StoreSettingDetailPage.valid_date_title')}
                            </Form.Label>
                            <br />
                            <div className="mb-3" style={{ maxWidth: '160px' }}>
                              <ReactDatePicker
                                locale="ja"
                                selected={formSearchValue.dataDate}
                                onChange={(date: Date) => {
                                  setFormSearchValue({ ...formSearchValue, dataDate: date });
                                }}
                                customInput={<CustomInputDatePicker />}
                                dateFormat="yyyy/MM/dd"
                                name="end_date"
                              />
                            </div>
                          </Col>
                        </Row>
                      </fieldset>
                    </Col>
                  </Row>
                </fieldset>
              </Col>

              <Col sm={1} className="text-xl-center text-xxl-start">
                <Button type="submit">{t('StoreSettingDetailPage.search')}</Button>
              </Col>
            </Row>
          </Accordion.Body>
          <div className="accordion-body accordion-summary">
            <Row>
              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('StoreSettingDetailPage.time_frame')}：</Form.Label>
                <Form.Label className="col-form-label">
                  {timeFrameSource?.find((timeFrame) => timeFrame.id.toString() === formSearchValue.timeFrameId)
                    ?.name || '-'}
                </Form.Label>
              </Col>

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('StoreSettingDetailPage.eating_way')}：</Form.Label>
                <Form.Label className="col-form-label">
                  {eatingWaySource?.find((eatingWay) => eatingWay.id.toString() === formSearchValue.eatingWayId)
                    ?.name || t('StoreSettingDetailPage.no_option')}
                </Form.Label>
              </Col>

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('StoreSettingDetailPage.eating_way_setting')}：</Form.Label>
                <Form.Label className="col-form-label">
                  {SHOW_ASSIGNED_SOURCE[formSearchValue.assignedStatus] || '-'}
                </Form.Label>
              </Col>

              {formSearchValue.assignedStatus === '1' && (
                <>
                  <Col sm={'auto'}>
                    <Form.Label className="col-form-label">
                      {t('StoreSettingDetailPage.assigned_time_frame')}：
                    </Form.Label>
                    <Form.Label className="col-form-label">
                      {assignedTimeFrameSource?.find(
                        (timeFrame) => timeFrame.id.toString() === formSearchValue.assignedTimeFrameId
                      )?.name || t('StoreSettingDetailPage.no_option')}
                    </Form.Label>
                  </Col>

                  <Col sm={'auto'}>
                    <Form.Label className="col-form-label">{t('StoreSettingDetailPage.menu_book')}：</Form.Label>
                    <Form.Label className="col-form-label">
                      {menuBookMap?.[formSearchValue.menuBookId]?.name || t('StoreSettingDetailPage.no_option')}
                    </Form.Label>
                  </Col>

                  <Col sm={'auto'}>
                    <Form.Label className="col-form-label">{t('StoreSettingDetailPage.course')}：</Form.Label>
                    <Form.Label className="col-form-label">
                      {courseMap?.[formSearchValue.courseId] || t('StoreSettingDetailPage.no_option')}
                    </Form.Label>
                  </Col>
                </>
              )}

              <Col sm={'auto'}>
                <Form.Label className="col-form-label">{t('EditMenuBookModal.deadline')}：</Form.Label>
                <Form.Label className="col-form-label">
                  {moment(formSearchValue.dataDate).format(DATE_FORMAT_YYYY_MM_DD)}
                </Form.Label>
              </Col>
            </Row>
          </div>
        </Accordion.Item>
      </Accordion>
    </form>
  );
}

export default StoreSettingDetailFilter;
